import httpService from "./http.service";
import { getAuthApiRoute } from "../utils/api.utils";

export const PUBLIC_AUTH_CONFIG_DEFAULTS = {
  maxProfileCount: 3,
  maxSessionCount: 3,
  serverTime: null,
};

const CONFIG_STORAGE_KEY = "webTvPublicAuthConfig";
const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;

var currentConfig = readStoredConfig();
var serverClockOffsetMs = 0;
var hasServerClock = false;
var lastFetchAt = 0;
var pendingRequest = null;
var subscribers = [];

function readStoredConfig() {
  try {
    var raw = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (!raw) return { ...PUBLIC_AUTH_CONFIG_DEFAULTS };

    var parsed = JSON.parse(raw);
    return normalizeConfig(parsed);
  } catch (error) {
    return { ...PUBLIC_AUTH_CONFIG_DEFAULTS };
  }
}

function persistConfig(config) {
  try {
    localStorage.setItem(
      CONFIG_STORAGE_KEY,
      JSON.stringify({
        maxProfileCount: config.maxProfileCount,
        maxSessionCount: config.maxSessionCount,
      })
    );
  } catch (error) {
    // Storage is best-effort on older TV browsers.
  }
}

function toPositiveNumber(value, fallback) {
  var normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) return fallback;
  return normalized;
}

function normalizeConfig(config) {
  var source = config || {};

  return {
    maxProfileCount: toPositiveNumber(
      source.maxProfileCount,
      PUBLIC_AUTH_CONFIG_DEFAULTS.maxProfileCount
    ),
    maxSessionCount: toPositiveNumber(
      source.maxSessionCount,
      PUBLIC_AUTH_CONFIG_DEFAULTS.maxSessionCount
    ),
    serverTime: Number.isFinite(Number(source.serverTime))
      ? Number(source.serverTime)
      : null,
  };
}

function notifySubscribers() {
  var snapshot = getPublicAuthConfigSync();
  subscribers.forEach(function (listener) {
    try {
      listener({
        config: snapshot,
        clockOffsetMs: serverClockOffsetMs,
        hasServerClock: hasServerClock,
      });
    } catch (error) {
      // Keep one listener from breaking other app surfaces.
    }
  });
}

function applyConfig(rawConfig, requestStartedAt, responseReceivedAt) {
  var nextConfig = normalizeConfig(rawConfig);
  currentConfig = nextConfig;
  lastFetchAt = responseReceivedAt || Date.now();

  if (nextConfig.serverTime !== null) {
    var midpoint = requestStartedAt
      ? Math.round((requestStartedAt + lastFetchAt) / 2)
      : lastFetchAt;
    serverClockOffsetMs = nextConfig.serverTime - midpoint;
    hasServerClock = true;
  }

  persistConfig(nextConfig);
  notifySubscribers();

  return getPublicAuthConfigSync();
}

export function getPublicAuthConfigSync() {
  return {
    ...PUBLIC_AUTH_CONFIG_DEFAULTS,
    ...currentConfig,
  };
}

export function getMaxProfileCount(config) {
  return toPositiveNumber(
    (config || currentConfig).maxProfileCount,
    PUBLIC_AUTH_CONFIG_DEFAULTS.maxProfileCount
  );
}

export function getMaxSessionCount(config) {
  return toPositiveNumber(
    (config || currentConfig).maxSessionCount,
    PUBLIC_AUTH_CONFIG_DEFAULTS.maxSessionCount
  );
}

export function getServerNow() {
  return Date.now() + serverClockOffsetMs;
}

export function getClockOffsetMs() {
  return serverClockOffsetMs;
}

export function isServerClockSynced() {
  return hasServerClock;
}

export function subscribePublicAuthConfig(listener) {
  subscribers.push(listener);

  return function unsubscribe() {
    subscribers = subscribers.filter(function (item) {
      return item !== listener;
    });
  };
}

export async function fetchPublicAuthConfig(options) {
  var force = options && options.force;
  var now = Date.now();

  if (!force && hasServerClock && now - lastFetchAt < CONFIG_CACHE_TTL_MS) {
    return getPublicAuthConfigSync();
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  var requestStartedAt = Date.now();

  var configRequest = httpService.get(getAuthApiRoute("config"));

  if (!configRequest || typeof configRequest.then !== "function") {
    return Promise.resolve(getPublicAuthConfigSync());
  }

  pendingRequest = configRequest
    .then(function (response) {
      var responseReceivedAt = Date.now();
      var responseData = response.data || {};
      var data = responseData.data || responseData;
      return applyConfig(data, requestStartedAt, responseReceivedAt);
    })
    .catch(function () {
      return getPublicAuthConfigSync();
    });

  pendingRequest = pendingRequest.then(function (config) {
    pendingRequest = null;
    return config;
  }, function (error) {
      pendingRequest = null;
      throw error;
  });

  return pendingRequest;
}

export function __setPublicAuthConfigForTests(config, clockOffset) {
  currentConfig = normalizeConfig(config);
  serverClockOffsetMs = Number(clockOffset) || 0;
  hasServerClock = currentConfig.serverTime !== null || serverClockOffsetMs !== 0;
  notifySubscribers();
}
